import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} MobileAtcOverlayRefs
 * @property {HTMLElement} overlayBar - The overlay bar container
 * @property {HTMLButtonElement} overlayButton - The overlay add-to-cart button
 * @property {HTMLElement | undefined} overlayWishlist - Optional wishlist slot next to overlay button
 */

/**
 * Mobile-only sticky "Add to bag" overlay for PDP.
 * Shows when the main buy-buttons block is scrolled out of view.
 * Delegates click to the real product form button.
 *
 * @extends {Component<MobileAtcOverlayRefs>}
 */
class MobileAtcOverlay extends Component {
  requiredRefs = ['overlayBar', 'overlayButton'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {AbortController} */
  #abort = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetButton = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {MutationObserver | null} */
  #wishlistObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // Only activate on mobile
    if (window.innerWidth > 749) return;

    this.#setupObserver();

    const { signal } = this.#abort;
    const section = this.closest('.shopify-section');

    // Sync variant updates (button text / disabled state)
    section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    // Listen for successful cart add to show "Added" feedback
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAdd, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError, { signal });
    document.addEventListener('cart-drawer:opened', this.#handleDrawerOpened, { signal });

    // GA tracking for wishlist clicks from the sticky overlay
    this.refs.overlayWishlist?.addEventListener('click', this.#handleWishlistClick, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#wishlistObserver?.disconnect();
    this.#wishlistObserver = null;
    this.#moveWishlistOut();
    this.#abort.abort();
    document.body.classList.remove('mobile-atc-overlay-active');
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
  }

  /* ------------------------------------------------------------------ */
  /*  Intersection Observer                                              */
  /* ------------------------------------------------------------------ */
  #setupObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    this.#targetButton = productForm.querySelector('[ref="addToCartButton"]');

    this.#observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (entry.isIntersecting) {
        // Main button is in view — hide overlay
        this.#hide();
      } else {
        // Main button is out of view — show overlay
        this.#show();
      }
    });

    this.#observer.observe(buyButtonsBlock);
  }

  /* ------------------------------------------------------------------ */
  /*  Show / Hide                                                        */
  /* ------------------------------------------------------------------ */
  #show() {
    // Don't show overlay if variant is out of stock (button disabled)
    if (this.refs.overlayButton.disabled) return;
    this.refs.overlayBar.dataset.visible = 'true';
    document.body.classList.add('mobile-atc-overlay-active');
    this.#moveWishlistIn();
  }

  #hide() {
    this.refs.overlayBar.dataset.visible = 'false';
    document.body.classList.remove('mobile-atc-overlay-active');
    this.#moveWishlistOut();
  }

  /* ------------------------------------------------------------------ */
  /*  Wishlist slot — move real element in/out of the sticky bar         */
  /* ------------------------------------------------------------------ */
  #moveWishlistIn() {
    const slot = this.refs.overlayWishlist;
    if (!slot) return;
    const wlh = document.getElementById('wishlisthero-product-page-button-container');
    if (wlh) {
      slot.appendChild(wlh);
      this.#applyWishlistStyles(wlh);
      return;
    }
    // WishlistHero hasn't rendered yet — watch for it to appear, then move it in
    if (this.#wishlistObserver) return;
    this.#wishlistObserver = new MutationObserver(() => {
      const el = document.getElementById('wishlisthero-product-page-button-container');
      if (!el) return;
      this.#wishlistObserver?.disconnect();
      this.#wishlistObserver = null;
      if (this.refs.overlayBar.dataset.visible === 'true') {
        slot.appendChild(el);
        this.#applyWishlistStyles(el);
      }
    });
    this.#wishlistObserver.observe(document.body, { childList: true, subtree: true });
  }

  #moveWishlistOut() {
    const portal = document.getElementById('atc-wishlist-portal');
    const wlh = document.getElementById('wishlisthero-product-page-button-container');
    if (portal && wlh && !portal.contains(wlh)) {
      portal.appendChild(wlh);
      this.#setOverlayGaClasses(wlh, false);
    }
  }

  /** Swap GA tracking classes between overlay and non-overlay variants. */
  #setOverlayGaClasses(/** @type {HTMLElement} */ wlh, /** @type {boolean} */ inOverlay) {
    const btn = wlh.querySelector('button');
    if (!btn) return;
    const isAdding = (btn.getAttribute('aria-label') ?? '').toLowerCase().includes('add');
    btn.classList.remove(
      'ga-pdp-wishlist-add-to-wishlist',
      'ga-pdp-wishlist-remove-to-wishlist',
      'ga-pdp-wishlist-add-to-wishlist-overlay',
      'ga-pdp-wishlist-remove-to-wishlist-overlay'
    );
    if (inOverlay) {
      btn.classList.add(isAdding ? 'ga-pdp-wishlist-add-to-wishlist-overlay' : 'ga-pdp-wishlist-remove-to-wishlist-overlay');
    } else {
      btn.classList.add(isAdding ? 'ga-pdp-wishlist-add-to-wishlist' : 'ga-pdp-wishlist-remove-to-wishlist');
    }
  }

  /** Swap SVG to thin stroke heart and apply overlay GA classes on move-in. */
  #applyWishlistStyles(/** @type {HTMLElement} */ wlh) {
    /** @type {any} */ (window)._swapWishlistSvg?.(wlh);
    // Restart svgWatcher scoped to the element at its new location in the overlay,
    // so it catches the SVG if WishlistHero renders it after the container appears.
    /** @type {any} */ (window)._startWishlistSvgWatcher?.();
    this.#setOverlayGaClasses(wlh, true);
  }

  #handleWishlistClick = () => {
    const wlh = document.getElementById('wishlisthero-product-page-button-container');
    if (!wlh) return;
    // Re-apply overlay classes on click — keeps the correct add/remove variant in sync.
    this.#setOverlayGaClasses(wlh, true);
  };

  /* ------------------------------------------------------------------ */
  /*  Click handler — delegates to real product form                     */
  /* ------------------------------------------------------------------ */
  handleClick = () => {
    if (!this.#targetButton || this.refs.overlayButton.disabled) return;

    // Mark as overlay so product-form tracks it as "PDP - Add to cart - overlay"
    this.#targetButton.dataset.puppet = 'true';

    // Delegate click to the real form button
    this.#targetButton.click();
  };

  /* ------------------------------------------------------------------ */
  /*  Analytics                                                          */
  /* ------------------------------------------------------------------ */
  // Not called anywhere — handleClick above delegates to the real form
  // button, whose own click already fires the (engraving-aware) dataLayer
  // push in assets/product-form.js. Calling this too would double-count
  // every overlay add-to-cart. Kept available in case it's wired up
  // deliberately later.
  #trackAddToCart() {
    const buttonText = this.refs.overlayButton?.textContent?.trim().toLowerCase() || '';
    const isPreorder = buttonText.includes('pre order') || buttonText.includes('preorder');
    const engravingComponent = /** @type {HTMLElement | null} */ (
      document.querySelector(`product-engraving-component[data-product-id="${this.dataset.productId}"]`)
    );
    const isEngraving = engravingComponent?.dataset.active === 'true';
    const atcLabel = isEngraving
      ? 'PDP - Add custom item to bag'
      : isPreorder
        ? 'PDP - Preorder - overlay'
        : 'PDP - Add to cart - overlay';

    // Push to dataLayer for GTM
    const dl = /** @type {any} */ (window).dataLayer;
    if (Array.isArray(dl)) {
      dl.push({
        event: 'add_to_cart_diff',
        add_to_cart_label: atcLabel,
      });
    }

    // Dispatch a custom event that other tracking scripts can listen to
    document.dispatchEvent(
      new CustomEvent('mobile-atc-overlay:add-to-cart', {
        bubbles: true,
        detail: {
          productId: this.dataset.productId,
          label: atcLabel,
        },
      })
    );
  }

  /* ------------------------------------------------------------------ */
  /*  Event handlers                                                     */
  /* ------------------------------------------------------------------ */

  /** @param {CustomEvent} event */
  #handleVariantUpdate = (event) => {
    if (String(event.detail.data?.productId) !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const newOverlay = event.detail.data?.html?.querySelector('mobile-atc-overlay');

    if (newOverlay) {
      const newButton = newOverlay.querySelector('[ref="overlayButton"]');
      if (newButton) {
        this.refs.overlayButton.disabled = newButton.disabled;
        const newText = newButton.querySelector('.mobile-atc-overlay__button-text');
        const currentText = this.refs.overlayButton.querySelector('.mobile-atc-overlay__button-text');
        if (newText && currentText) {
          currentText.textContent = newText.textContent;
        }
      }
    }

    // The morphed markup above is always the server-rendered default
    // ("Add to bag"), with no knowledge of engraving state — if engraving
    // is currently active for this product, re-apply its CTA text so a
    // variant switch mid-engraving doesn't silently revert the button.
    const engravingComponent = /** @type {HTMLElement | null} */ (
      document.querySelector(`product-engraving-component[data-product-id="${this.dataset.productId}"]`)
    );
    if (engravingComponent?.dataset.active === 'true') {
      const currentText = this.refs.overlayButton.querySelector('.mobile-atc-overlay__button-text');
      const customText = engravingComponent.dataset.addCustomItemText;
      if (currentText && customText) currentText.textContent = customText;
    }

    // Re-cache target button after variant morph
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.refs.overlayButton.disabled = true;
    }

    // Hide overlay if variant is out of stock, show if available and main button not in view
    if (this.refs.overlayButton.disabled) {
      this.#hide();
    }
  };

  #handleCartAdd = () => {
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.refs.overlayButton.dataset.added = 'true';

    this.#resetTimeout = setTimeout(() => {
      this.refs.overlayButton.removeAttribute('data-added');
    }, 800);
  };

   #handleCartError = () => {
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.refs.overlayButton.removeAttribute('data-added');
  };

  #handleDrawerOpened = () => {
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.refs.overlayButton.removeAttribute('data-added');
  };


  /* ------------------------------------------------------------------ */
  /*  Helpers                                                            */
  /* ------------------------------------------------------------------ */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;
    const section = this.closest('.shopify-section');
    if (!section) return null;
    const sectionId = section.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }
}

if (!customElements.get('mobile-atc-overlay')) {
  customElements.define('mobile-atc-overlay', MobileAtcOverlay);
}
